import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import type { ResultEnvelope, Visualization } from "@cd/core";
import { delta, formatValue } from "@cd/viz";
import { describeRange } from "../../lib/time";
import { cn } from "../../lib/utils";

/** Stat tile: label · value · signed delta vs a named period. */
export function Kpi({ viz, result }: { viz: Visualization; result: ResultEnvelope }) {
  const metrics = result.columns.filter((c) => c.kind === "metric");
  const row = result.rows[0] ?? {};
  const prev = result.compareRows?.[0];
  const upIsGood = viz.config.upIsGood !== false;
  return (
    <div className={cn("grid h-full content-center gap-4", metrics.length > 1 ? "grid-cols-2" : "grid-cols-1")}>
      {metrics.map((m, i) => {
        const d = prev ? delta(row[m.key], prev[m.key]) : null;
        const good = d !== null && (d > 0) === upIsGood && Math.abs(d) >= 0.05;
        const flat = d !== null && Math.abs(d) < 0.05;
        const Icon = flat ? Minus : d !== null && d > 0 ? ArrowUpRight : ArrowDownRight;
        return (
          <div key={m.key} className="min-w-0">
            {metrics.length > 1 && <div className="truncate text-xs text-muted">{m.label}</div>}
            <div className={cn("font-semibold tracking-tight", i === 0 && metrics.length === 1 ? "text-4xl" : "text-2xl")}>
              {formatValue(row[m.key], m.format, { compact: true })}
            </div>
            {d !== null && (
              <div className="mt-1.5 flex items-center gap-1 text-xs">
                <span className={cn("inline-flex items-center gap-0.5 font-medium", flat ? "text-muted" : good ? "text-good" : "text-bad")}>
                  <Icon className="size-3.5" aria-hidden />
                  {d > 0 ? "+" : ""}
                  {d.toFixed(1)}%
                </span>
                <span className="text-subtle">
                  vs {result.compareTimeRange ? describeRange(result.compareTimeRange) : "previous"} (
                  {formatValue(prev?.[m.key], m.format, { compact: true })})
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
